import { Card, CardContent, CardHeader, Typography } from "@mui/material";
import { Fragment, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@common/Modal/Modal";
import { MoreAboutEsim } from "./MoreAboutEsim";
import { TITLE_INHERIT_STYLE } from "@constants";
import { ReplacementSimForm } from "./ReplacementSimForm";
import { ReplacementESim } from "@features/ManageSim/ReplacementESim";
import { ChoiceDisplay, EChoiceDisplayValues } from "@common/ChoiceDisplay";
import { EChoiceDisplayMode } from "@common/ChoiceDisplay/EChoiceDisplayMode";
import { useFeatureFlag } from "@common/FeatureFlagProvider/useFeatureFlag";
import { featureFlagNameList } from "@common/FeatureFlag/FeatureFlagNameList";

export const ReplacementSim = () => {
    const { t } = useTranslation(["sim", "glossary"]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [simSwapChoice, setSimSwapChoice] = useState<EChoiceDisplayValues>(EChoiceDisplayValues.NONE);

    const { getIsFeatureEnabled } = useFeatureFlag();
    const isReplacementESimEnabled = getIsFeatureEnabled(featureFlagNameList.replacementEsim);

    const closeModal = () => setIsModalOpen(false);
    const displayModal = () => setIsModalOpen(true);

    const closeForm = () => setSimSwapChoice(EChoiceDisplayValues.NONE);

    const onSuccessCallback = useCallback(() => {
        closeForm();
        displayModal();
    }, []);

    const renderReplacementSimForm = useCallback(() => {
        if (!isReplacementESimEnabled) {
            return <ReplacementSimForm closeForm={closeForm} onSuccessCallback={onSuccessCallback} />;
        }

        return (
            <>
                <Typography className="replacementSim-chooseSimType">{t("sim:chooseSimType")}</Typography>
                <ChoiceDisplay
                    mode={EChoiceDisplayMode.RADIO}
                    options={[
                        {
                            label: t("sim:physicalSim"),
                            value: EChoiceDisplayValues.FIRST,
                            component: (
                                <ReplacementSimForm closeForm={closeForm} onSuccessCallback={onSuccessCallback} />
                            ),
                        },
                        {
                            label: t("sim:eSIM"),
                            value: EChoiceDisplayValues.SECOND,
                            component: <ReplacementESim closeForm={closeForm} onSuccessCallback={onSuccessCallback} />,
                            helper: <MoreAboutEsim />,
                        },
                    ]}
                    selectedChoice={simSwapChoice}
                    setSelectedChoice={setSimSwapChoice}
                />
            </>
        );
    }, [isReplacementESimEnabled, onSuccessCallback, simSwapChoice, t]);

    return (
        <Fragment>
            <Card className="replacementSim" elevation={2}>
                <CardHeader
                    className="card-header card-header__right"
                    title={t("sim:replaceSim")}
                    titleTypographyProps={TITLE_INHERIT_STYLE}
                />
                <CardContent className="replacementSim-content">
                    <div className="card-header__circle" />
                    {renderReplacementSimForm()}
                </CardContent>
            </Card>
            <Modal
                close={closeModal}
                confirm={closeModal}
                confirmLabel={t("glossary:close")}
                isModalOpen={isModalOpen}
                title={t("glossary:happyDays")}
            >
                <Typography align="center" variant="normal">
                    {t("sim:replacementSimSuccess")}
                </Typography>
            </Modal>
        </Fragment>
    );
};
